import { type PortfolioStatusReportsQuery, STATUS_REPORT_STATUSES } from '@sahab/contracts'
import { z } from 'zod'

export const REPORT_PAGE_SIZES = [25, 50, 100] as const

const pageSize = z
  .number()
  .refine((n): n is (typeof REPORT_PAGE_SIZES)[number] =>
    (REPORT_PAGE_SIZES as readonly number[]).includes(n),
  )
  .optional()
  .catch(undefined)
const page = z.number().int().min(1).optional().catch(undefined)
const q = z.coerce.string().trim().max(200).optional().catch(undefined)

/** A week is named by its Sunday (P7 decision 1): anything else is dropped from the URL. */
const sunday = z.iso
  .date()
  .refine((d) => new Date(`${d}T00:00:00Z`).getUTCDay() === 0)
  .optional()
  .catch(undefined)

/**
 * The project's reports (`/projects/:id/reports`): words in the four sections and the page, named
 * apart from the workspace's own keys.
 */
export const projectReportsSearchSchema = z.object({ reportsQ: q, reportsPage: page })
export type ProjectReportsSearch = z.infer<typeof projectReportsSearchSchema>

export const READING_SORTS = ['project', '-project', 'status', '-status'] as const

/**
 * The director's weekly reading (`/projects/status-reports`) in the URL: the week (the last complete
 * one when absent), the Missing view, the filters, the sort and the page.
 */
export const weeklyReadingSearchSchema = z.object({
  week: sunday,
  /** The project's code or name. */
  q,
  view: z.enum(['missing']).optional().catch(undefined),
  status: z.enum(STATUS_REPORT_STATUSES).optional().catch(undefined),
  managerId: z.uuid().optional().catch(undefined),
  sort: z.enum(READING_SORTS).optional().catch(undefined),
  page,
  pageSize,
})
export type WeeklyReadingSearch = z.infer<typeof weeklyReadingSearchSchema>

export function toReadingQuery(search: WeeklyReadingSearch, week: string): PortfolioStatusReportsQuery {
  return {
    week,
    q: search.q,
    status: search.status,
    managerId: search.managerId,
    sort: search.sort ?? 'project',
    page: search.page ?? 1,
    pageSize: search.pageSize ?? 50,
  }
}
